"use client";
// beui.dev/components/motion/tooltip (surface half)
// Restyled for Bavimail (DESIGN.md floating panels): sharp `rounded-sm` raised
// surface, hairline `hb-card` border, Chivo Mono 10px label, modal-grade shadow.
// Portalled to <body>, so it carries its own `font-mono-hb`. Upstream's blur +
// offset "spawn" is replaced by the quiet fade + slight scale that `modal.tsx`
// uses, on the shared panel spring. No arrow (upstream has none either).
// Bavimail addition: `TOOLTIP_SURFACE_CLASS` is exported so a non-motion host
// (e.g. recharts' `<Tooltip content>`) can borrow the exact look.

import { motion, useReducedMotion, type Variants } from "motion/react";
import { useMemo, type ComponentProps, type ReactNode, type Ref } from "react";
import { EASE_OUT, SPRING_PANEL } from "@/lib/ease";
import { cn } from "@/lib/utils";

type Side = "top" | "right" | "bottom" | "left";

/** The branded tooltip look, for hosts that render their own tooltip element. */
export const TOOLTIP_SURFACE_CLASS =
  "font-mono-hb block whitespace-nowrap rounded-sm border border-hb-card bg-hb-raised px-2 py-1 text-[10px] leading-snug text-hb-text shadow-xl shadow-black/40";

// Quiet entrance: opacity 0 -> 1, scale 0.98 -> 1, from the edge that faces the
// trigger (the caller sets `transformOrigin` per side). Exit is a short fade.
const VARIANTS: Variants = {
  initial: { opacity: 0, scale: 0.98 },
  animate: {
    opacity: 1,
    scale: 1,
    transition: { scale: SPRING_PANEL, opacity: { duration: 0.14, ease: EASE_OUT } },
  },
  exit: { opacity: 0, scale: 0.98, transition: { duration: 0.16, ease: EASE_OUT } },
};

const REDUCED_VARIANTS: Variants = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.14, ease: EASE_OUT } },
  exit: { opacity: 0, transition: { duration: 0.1, ease: EASE_OUT } },
};

/** The shared visual surface for trigger tooltips and chart readouts. Positioning belongs to the caller. */
export function TooltipSurface({
  children,
  side = "top",
  className,
  ref,
  ...props
}: Omit<ComponentProps<typeof motion.span>, "children"> & {
  children?: ReactNode;
  side?: Side;
  ref?: Ref<HTMLSpanElement>;
}) {
  const reduce = useReducedMotion();
  const variants = useMemo(() => (reduce ? REDUCED_VARIANTS : VARIANTS), [reduce]);
  return (
    <motion.span
      ref={ref}
      role="tooltip"
      data-side={side}
      variants={variants}
      initial="initial"
      animate="animate"
      exit="exit"
      className={cn(TOOLTIP_SURFACE_CLASS, className)}
      {...props}
    >
      {children}
    </motion.span>
  );
}
